// A free receipt: stock in with no order behind it. For something bought in person.
// It is recorded in the same shape as an order (vendor, lines, price paid, the receipt
// itself attached later), so its cost is captured the same way; it just has no number and
// is received the moment it is written.

import { useState } from 'react';
import { toast } from '../../lib/api';
import { freeReceipt, useInventory, useVendors } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Btn, Empty, Field, Ln, Modal } from '../../ui/kit';

interface Line {
  material_id: string;
  qty: string;
  price: string;
}

export function FreeReceipt({ onClose, materialId, onDone }: { onClose: () => void; materialId?: string; onDone?: (orderId: string) => void }) {
  const s = useSession();
  const inv = useInventory();
  const vendors = useVendors();
  const [vendorId, setVendorId] = useState('');
  const [note, setNote] = useState('');
  const [find, setFind] = useState('');
  const [lines, setLines] = useState<Line[]>(materialId ? [{ material_id: materialId, qty: '', price: '' }] : []);
  const [busy, setBusy] = useState(false);

  const words = find.trim().toLowerCase();
  const matches = words.length < 2 ? [] : inv.materials.filter((m) => !lines.some((l) => l.material_id === m.id) && `${m.description} ${m.part_number ?? ''}`.toLowerCase().includes(words)).slice(0, 8);
  const set = (i: number, patch: Partial<Line>) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const ready = lines.length > 0 && lines.every((l) => Number(l.qty) > 0 && (l.price.trim() === '' || Number(l.price) >= 0));

  const submit = async () => {
    setBusy(true);
    try {
      const id = await freeReceipt(vendorId || null, lines.map((l) => ({ material_id: l.material_id, qty: Number(l.qty), unit_price: l.price.trim() === '' ? null : Number(l.price) })), note.trim() || null, s.user?.id ?? null);
      toast('Received into stock.');
      onDone?.(id);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Free receipt · bought in person, no order" onClose={onClose}>
      <div className="stack">
        <small>Use this only for an in-store purchase. Anything ordered goes on a purchase order and is received against it.</small>
        <div className="form">
          <Field label="Where it was bought">
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">not recorded</option>
              {vendors.rows.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Note">
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
          </Field>
        </div>

        <div>
          {lines.length === 0 && <Empty>Find what was bought, below.</Empty>}
          {lines.map((l, i) => {
            const m = inv.materialById.get(l.material_id);
            return (
              <Ln
                key={l.material_id}
                left={
                  <>
                    {m?.description ?? 'material'} {m?.tracking_mode === 'bin' && <small>· counted by the bin, so no count changes</small>}
                  </>
                }
                right={
                  <span className="nowrap">
                    <input type="number" className="short" style={{ width: 84 }} min="0" step="any" placeholder={`qty ${m?.unit ?? ''}`} value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} aria-label="Quantity" />{' '}
                    {s.can('see_prices') && <input type="number" className="short" style={{ width: 104 }} min="0" step="any" placeholder="price each" value={l.price} onChange={(e) => set(i, { price: e.target.value })} aria-label="Price paid each" />}{' '}
                    <button type="button" className="linklike" onClick={() => setLines(lines.filter((_, k) => k !== i))}>
                      remove
                    </button>
                  </span>
                }
              />
            );
          })}
        </div>

        <div>
          <input type="search" placeholder="Find a material to add (two letters or more)" value={find} onChange={(e) => setFind(e.target.value)} style={{ width: '100%' }} />
          {matches.map((m) => (
            <Ln
              key={m.id}
              onClick={() => {
                setLines([...lines, { material_id: m.id, qty: '', price: '' }]);
                setFind('');
              }}
              left={
                <>
                  {m.description} {m.part_number && <small>· {m.part_number}</small>}
                </>
              }
              right={<small>add</small>}
            />
          ))}
          {words.length >= 2 && matches.length === 0 && <small>No material matches.</small>}
        </div>

        <div>
          <Btn primary disabled={!ready || busy} onClick={() => void submit()}>
            {busy ? 'Receiving…' : 'Receive into stock'}
          </Btn>{' '}
          <small>The price paid can be left for later; it is on the receipt's record with the rest.</small>
        </div>
      </div>
    </Modal>
  );
}
